import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App.jsx";
import "./index.css";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import "./index.css";

// NOTE: Leaflet's setup (tile/cluster CSS + marker-icon fix) used to be imported
// here eagerly, which forced Leaflet's core JS (~147KB) and CSS (~316KB, render-
// blocking) into the entry chunk on EVERY page. It's now imported inside each map
// component (all of which are lazy route chunks), so Leaflet loads only when a map
// actually renders. See src/common/components/leaflet/leafletSetup.js.

// Fonts (DM Sans / Playfair Display / Cormorant Garamond) are loaded once via
// the <link> in index.html. The @fontsource imports that used to live here were
// removed so the same font isn't requested a second/third time.

import TranslationProvider from "./common/context/TranslationContext";
import MapContextProvider from "./common/context/MapContext.jsx";
import { HelmetProvider } from "react-helmet-async";
import ReactQueryProvider from "./providers/QueryProvider.jsx";

// Side-effect import: fires `mixpanel.init()` once on app boot so every
// downstream `track()` / `identify()` call has a live SDK to talk to.
// Without this import the lib is never loaded and Mixpanel stays silent.
import "./lib/mixpanel";

// Side-effect import: stops a focused <input type="number"> (Document No,
// Registration Year, …) from changing value when the user scrolls over it.
import "./common/utils/numberInputWheelGuard";

// Recover from stale lazy-chunk loads after a deploy.
// When a new build ships, the old hash-named route chunks (e.g.
// `Party-a1b2c3.js`) are deleted from the server. A tab that was open
// across the deploy still references the old filenames, so the next
// lazy `import()` 404s and Vite fires `vite:preloadError`. We force one
// fresh load to pull the new index.html (and new chunk names) instead of
// dropping the user on the 404 page.
window.addEventListener("vite:preloadError", (event) => {
  event.preventDefault(); // stop Vite from rethrowing the load error
  const KEY = "vite:lastChunkReload";
  const last = Number(sessionStorage.getItem(KEY) || 0);
  const now = Date.now();
  // Only auto-reload if we haven't already done so very recently, so a
  // genuinely broken deploy can't trap the user in an endless reload loop.
  if (now - last > 10000) {
    sessionStorage.setItem(KEY, String(now));
    window.location.reload();
  }
});

const rootElement = document.getElementById("root");

const app = (
  <React.StrictMode>
    <HelmetProvider>
      <ReactQueryProvider>
        <Provider store={store}>
          <TranslationProvider>
            <MapContextProvider>
              <App />
            </MapContextProvider>
          </TranslationProvider>
        </Provider>
      </ReactQueryProvider>
    </HelmetProvider>
  </React.StrictMode>
);

// When the page was prerendered at build time (react-snap), the #root already
// contains server-rendered markup — hydrate it instead of throwing it away, so
// the crawler-visible HTML (title/meta/OG that react-snap baked in) is reused
// and there's no flash of empty content. Fresh (non-prerendered) loads take the
// normal createRoot path.
// Read by pages that render differently by screen size (the home page): their
// first render must match the prerendered (desktop) markup when hydrating.
window.__JAAGA_PRERENDERED__ = rootElement.hasChildNodes();
if (rootElement.hasChildNodes()) {
  ReactDOM.hydrateRoot(rootElement, app);
} else {
  ReactDOM.createRoot(rootElement).render(app);
}
